/**
 * Build Your Set (PRO-14827) — global Alpine store shared by every page section.
 *
 * Sections (build-set-feature / -builder / -presets / -banner) are each a small
 * Alpine component reading `$store.buildSet`, so flipping the set-type toggle in
 * one section updates the grid, summary, hero and banner in the others.
 *
 * `window.BuildSetData` is emitted once by sections/build-set-builder.liquid.
 *
 * Cart contract (mirrors assets/box-builder.js addToCart): a single /cart/add.js
 * with the child variants (properties._bundle + _id) plus the set parent variant
 * (properties._bundle + _bundle_skus). The `cart-transformer` Shopify Function
 * keys off _bundle_skus to find the parent and groups children by _bundle.
 * Both the self-built set and a locked curated set use this same payload shape.
 */

/**
 * @typedef {Object} BuildSetProduct
 * @property {string} id - Product id, as a string (matches the keys of `products`)
 * @property {number} variantId - The single variant's id, sent to /cart/add.js
 * @property {string} sku - Used to build the parent's _bundle_skus attribute
 * @property {string} title
 * @property {string} handle
 * @property {number} price - In cents
 * @property {number|null} compareAtPrice - In cents, null when not marked down
 * @property {boolean} available - False renders the card as sold out and unselectable
 * @property {string} image
 */

/**
 * @typedef {Object} BuildSetPreset
 * @property {string} handle - Metaobject handle; the id passed to addCuratedSetToCart
 * @property {string} name
 * @property {BuildSetProduct[]} products - Full records; curated scents need not be in the pool
 */

/**
 * @typedef {Object} BuildSetType
 * @property {string} key
 * @property {number} bundleSize - Number of slots
 * @property {string} formatLabel
 * @property {string} slotLabel
 * @property {string} summarySubtitle
 * @property {string|null} redirectUrl - Mini Tin launch fallback; navigates instead of switching
 * @property {number|null} parentVariantId - The $0/bundle parent variant
 * @property {number|null} bundlePrice - In cents, display only
 * @property {number|null} comparePrice - In cents, display only
 * @property {Object<string, BuildSetProduct>} products
 * @property {string[]} productOrder
 * @property {Object<string, BuildSetPreset>} presets
 */

/**
 * @typedef {Object} BuildSetLabels
 * @property {string} addToCart
 * @property {string} addMore - Contains a [count] placeholder
 * @property {string} adding
 */

/**
 * @typedef {Object} BuildSetData
 * @property {string|null} defaultSetType
 * @property {BuildSetLabels} labels
 * @property {Object<string, BuildSetType>} setTypes
 */

/**
 * @typedef {Object} CartLineItem
 * @property {number} id - Variant id
 * @property {number} quantity
 * @property {Object<string, string>} properties
 */

document.addEventListener('alpine:init', () => {
  /** @type {BuildSetData} */
  const data = window.BuildSetData || { defaultSetType: null, labels: {}, setTypes: {} };

  Alpine.store('buildSet', {
    data,
    activeSetType: data.defaultSetType || Object.keys(data.setTypes)[0] || null,
    /**
     * Selections are kept PER SET TYPE so flipping the toggle never loses work:
     * { 'edp-trio': [id, null, null], 'mini-tin': [null, null, null] }
     * @type {Object<string, Array<string|null>>}
     */
    selections: {},
    addingToCart: false,
    /** @type {string|null} Handle of the curated set currently being added */
    addingPreset: null,
    sheetOpen: false,

    /**
     * Seeds an empty slot array for every set type. Alpine calls this
     * automatically once the store is registered.
     * @returns {void}
     */
    init() {
      for (const key of Object.keys(this.data.setTypes)) {
        this.selections[key] = this.emptySlots(key);
      }
      this.buildSlotClips();
      this.trackHeaderOffset();
    },

    /**
     * Park the desktop summary below the theme's fixed header. The header's
     * height changes with the viewport (198px at 1366 wide, a different header
     * below its desktop breakpoint), so a fixed sticky top hid the panel's top
     * behind it. The theme already pads <body> by exactly that height, so that
     * padding is the offset. Re-read on load (the theme may set it late) and on
     * resize; a 0 reading keeps the CSS default.
     * @returns {void}
     */
    trackHeaderOffset() {
      const column = document.querySelector('.build-set__summary-col');
      if (!column) return;

      const update = () => {
        const header = parseFloat(getComputedStyle(document.body).paddingTop) || 0;
        if (!header) return;
        const gap = parseFloat(getComputedStyle(column).getPropertyValue('--bs-gap')) || 0;
        column.style.setProperty('--bs-summary-sticky-top', `${header + gap}px`);
      };

      // debounced so the theme has re-padded <body> before we read it
      let timer;
      const settle = () => {
        clearTimeout(timer);
        timer = setTimeout(update, 200);
      };

      update();
      window.addEventListener('load', update, { once: true });
      window.addEventListener('resize', settle, { passive: true });
    },

    /**
     * Read a product photo and report where the product actually sits in it.
     * Shared by the slot fit and by the synthesised outline below.
     * @param {string} url
     * @returns {Promise<{left: number, top: number, boxWidth: number, boxHeight: number,
     *   naturalWidth: number, naturalHeight: number}|null>}
     */
    async measureImage(url) {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.src = url;
      await image.decode();

      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      context.drawImage(image, 0, 0);
      const { data } = context.getImageData(0, 0, canvas.width, canvas.height);

      let left = canvas.width;
      let top = canvas.height;
      let right = 0;
      let bottom = 0;
      let widestRow = 0;
      let widestLeft = 0;
      let widestWidth = 0;
      for (let y = 0; y < canvas.height; y++) {
        let rowLeft = -1;
        let rowRight = -1;
        for (let x = 0; x < canvas.width; x++) {
          const i = (y * canvas.width + x) * 4;
          if (data[i + 3] < 10) continue;
          // anything that is not near-white counts as product
          if (data[i] > 234 && data[i + 1] > 234 && data[i + 2] > 234) continue;
          if (rowLeft < 0) rowLeft = x;
          rowRight = x;
        }
        if (rowLeft < 0) continue;
        if (rowLeft < left) left = rowLeft;
        if (rowRight > right) right = rowRight;
        if (y < top) top = y;
        bottom = y;
        if (rowRight - rowLeft + 1 > widestWidth) {
          widestWidth = rowRight - rowLeft + 1;
          widestLeft = rowLeft;
          widestRow = y;
        }
      }
      const boxWidth = right - left + 1;
      const boxHeight = bottom - top + 1;
      if (boxWidth < 2 || boxHeight < 2) return null;

      /* A tin shot from above is a circle sitting on its own reflection, so the
         ink box is taller than the tin - 388 wide by 439 tall on the Mini Tin
         photography. The circle itself is the widest row's width, starting at the
         first inked row; measured against the real files the width profile runs
         29 -> 388 -> 62 across exactly that span, which is a circle. Taking the
         diameter rather than the ink box keeps the reflection out of the frame. */
      return {
        left,
        top,
        boxWidth,
        boxHeight,
        naturalWidth: canvas.width,
        naturalHeight: canvas.height,
        circle: { left: widestLeft, top, size: widestWidth, row: widestRow },
      };
    },

    /**
     * Draw an empty-slot outline for a set type that has no artwork of its own.
     *
     * The EDP slots use a hand-drawn bottle vector. Mini Tin has none, and a tin
     * photographed from above is a circle, so one can be derived from the product
     * itself. The viewBox is 44 wide to match the EDP artwork, which keeps the
     * 0.73 stroke weight identical between the two set types.
     * @param {{boxWidth: number, boxHeight: number, circle: {size: number}}} measured
     * @returns {{markup: string, round: boolean}}
     */
    outlineFor(measured) {
      const round = measured.boxWidth / measured.boxHeight > 0.5;
      const width = 44;
      const stroke = 0.73;
      // a round product is framed by its diameter, so the box is square
      const height = round
        ? width
        : +(width / (measured.boxWidth / measured.boxHeight)).toFixed(2);
      const markup =
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" fill="none">` +
        `<ellipse cx="${width / 2}" cy="${height / 2}" rx="${(width - stroke) / 2}"` +
        ` ry="${(height - stroke) / 2}" stroke="black" stroke-width="${stroke}"/></svg>`;
      return { markup, round };
    },

    /**
     * Turn each set type's slot artwork into a CSS clip path.
     *
     * The artwork is a stroke-only SVG (`fill="none"`), so it cannot be used as
     * a CSS mask - a mask reads alpha and would clip the photo down to the
     * 0.73px stroke. A clip path uses the path's geometry and ignores fill, so
     * the same file gives the full bottle silhouette.
     *
     * `clip-path: url()` cannot reference an external file in Chrome or Safari,
     * hence fetching it and injecting a local <clipPath>. Normalising it with
     * clipPathUnits="objectBoundingBox" plus a scale transform means one
     * definition fits whatever box the slot happens to be, rotation included,
     * so desktop and mobile share it.
     *
     * Failure is silent by design: no clip means an unclipped photo, which is
     * still a usable slot.
     * @returns {Promise<void>}
     */
    async buildSlotClips() {
      const entries = Object.entries(this.data.setTypes);
      if (!entries.length) return;

      const defs = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      defs.setAttribute('aria-hidden', 'true');
      defs.setAttribute('width', '0');
      defs.setAttribute('height', '0');
      defs.style.position = 'absolute';
      document.body.appendChild(defs);

      for (const [key, setType] of entries) {
        try {
          let markup;
          if (/\.svg(\?|$)/i.test(setType.placeholderImage || '')) {
            const response = await fetch(setType.placeholderImage);
            if (!response.ok) continue;
            markup = await response.text();
          } else {
            // no artwork for this set type: derive it from the first product
            const first = Object.values(setType.products || {})[0];
            if (!first || !first.image) continue;
            const measured = await this.measureImage(first.image);
            if (!measured) continue;
            const outline = this.outlineFor(measured);
            markup = outline.markup;
            setType.placeholderImage =
              'data:image/svg+xml;utf8,' + encodeURIComponent(outline.markup);
          }

          const doc = new DOMParser().parseFromString(markup, 'image/svg+xml');
          const root = doc.querySelector('svg');
          const shapes = [...doc.querySelectorAll('path, ellipse, circle, rect')];
          if (!root || !shapes.length) continue;

          const box = (root.getAttribute('viewBox') || '').split(/[\s,]+/).map(Number);
          const width = box.length === 4 ? box[2] : parseFloat(root.getAttribute('width'));
          const height = box.length === 4 ? box[3] : parseFloat(root.getAttribute('height'));
          if (!width || !height) continue;

          const clip = document.createElementNS('http://www.w3.org/2000/svg', 'clipPath');
          clip.setAttribute('id', `BuildSetSlotClip-${key}`);
          clip.setAttribute('clipPathUnits', 'objectBoundingBox');
          clip.setAttribute('transform', `scale(${1 / width}, ${1 / height})`);
          for (const shape of shapes) {
            clip.appendChild(shape.cloneNode(true));
          }
          defs.appendChild(clip);
          setType.clipId = `BuildSetSlotClip-${key}`;
          /* The frame has to take the artwork's own proportions. A fixed box plus
             object-fit: contain letterboxes the outline, which leaves the outline's
             base floating above the box's base - and the photo, which fills the box,
             then sits below the line the outline draws. Matching the ratio makes the
             two share one edge. */
          setType.slotAspect = `${width} / ${height}`;
          /* Read off the finished artwork, whichever source it came from - an
             uploaded vector has to behave the same as a synthesised one. A round
             frame means the product is seated by its circle rather than its ink
             box, so a tin's reflection stays out of it. */
          setType.slotRound = width / height > 0.5;
          /* The mobile row lays a tall bottle on its side. A tin is nearly round,
             so turning it would be meaningless - it stays upright and simply sits
             at the row's height. */
          setType.slotTurn = setType.slotRound ? '0deg' : '90deg';
        } catch (error) {
          console.warn('[build-set] slot clip unavailable for', key, error);
        }
      }
    },

    /**
     * @param {string} id - Product id in the slot
     * @returns {string} That product's image, or '' when the slot is empty
     */
    slotImage(id) {
      const product = this.product(id);
      return product ? product.image : '';
    },

    /**
     * Measured crops, keyed by image URL. A product photo is mostly empty space -
     * the Rosalie 6ml shot is 42% margin on each side - so fitting the PHOTO to
     * the outline fits all that whitespace too and the bottle lands small and
     * floating. Measuring where the bottle actually is lets us fit the bottle.
     * @type {Object<string, {zoom: number, x: number, y: number}>}
     */
    photoFits: {},

    /**
     * Find the product's bounding box inside its photo and work out the zoom and
     * offset that seat it in the slot: scaled so its height fills the outline,
     * centred across, and standing on the base line.
     *
     * Reads the bitmap through a canvas, so it needs the CDN's CORS headers;
     * Shopify sends them. Any failure leaves the URL unmeasured, which the CSS
     * reads as "no crop" - a usable slot, just not a seated one.
     * @param {string} url - Product image URL
     * @returns {Promise<void>}
     */
    async measurePhoto(url) {
      if (!url || this.photoFits[url]) return;
      this.photoFits[url] = null; // claim it so parallel slots don't re-measure

      try {
        const measured = await this.measureImage(url);
        if (!measured) {
          delete this.photoFits[url];
          return;
        }
        /* Store the raw measurement only. Turning it into a zoom and an offset
           needs the frame's proportions, and buildSlotClips() may not have
           resolved those yet - measuring against the fallback produced a bottle
           scaled by the wrong ratio. slotPhotoStyle() does that arithmetic at the
           moment the style is read, by which point the frame is known. */
        this.photoFits[url] = measured;
      } catch (error) {
        // drop the claim so a transient failure does not disable the fit for good
        delete this.photoFits[url];
        console.warn('[build-set] could not measure', url, error);
      }
    },

    /**
     * @param {string} id - Product id in the slot
     * @returns {string} Custom properties seating that product in the outline
     */
    slotPhotoStyle(id) {
      const url = this.slotImage(id);
      const fit = this.photoFits[url];
      if (fit === undefined) this.measurePhoto(url);
      if (!fit) return '';

      const [frameW, frameH] = (this.current && this.current.slotAspect
        ? this.current.slotAspect.split('/')
        : [44, 272]
      ).map(Number);

      /* Map the product's box onto the frame exactly rather than fitting one axis
         and leaving a gap on the other. The outline and the photographed bottle
         are within ~4% of each other - the outline carries a 0.73 stroke, the
         photo a soft edge - so scaling the axes independently is invisible on a
         bottle and makes it reach all four edges, which is the design.

         zoom  = width multiplier, in frame-widths
         ratio = the element ratio that puts the box at frame height too
         x, y  = translate() takes a share of the element's own box, and the
                 scale factors cancel out of that share entirely */
      /* Seat whatever the outline was drawn around. For a round set type that is
         the tin's circle, not the ink box - otherwise the reflection is counted as
         product and the tin floats up out of the circle. */
      const round = this.current && this.current.slotRound && fit.circle;
      const boxLeft = round ? fit.circle.left : fit.left;
      const boxTop = round ? fit.circle.top : fit.top;
      const boxWidth = round ? fit.circle.size : fit.boxWidth;
      const boxHeight = round ? fit.circle.size : fit.boxHeight;

      const zoom = fit.naturalWidth / boxWidth;
      const ratio =
        ((fit.naturalWidth * boxHeight) / (fit.naturalHeight * boxWidth)) * (frameW / frameH);

      return (
        `--bs-photo-zoom: ${zoom.toFixed(4)};` +
        ` --bs-photo-ratio: ${ratio.toFixed(5)};` +
        ` --bs-photo-x: ${((-boxLeft / fit.naturalWidth) * 100).toFixed(3)}%;` +
        ` --bs-photo-y: ${((-boxTop / fit.naturalHeight) * 100).toFixed(3)}%;`
      );
    },

    /** @returns {string} A `clip-path` value for the active set type's slots */
    get slotClip() {
      return this.current && this.current.clipId ? `url(#${this.current.clipId})` : 'none';
    },

    /**
     * Inline custom properties tying a slot to its artwork.
     *
     * The mobile row is 43px deep, and the artwork keeps its own shape inside it:
     * a bottle lies along the row, a tin stands upright in it. The frame that
     * follows from that, and the half-offsets the photo transform rotates about,
     * are pure arithmetic on the artwork's ratio - so they are emitted here even
     * though only the mobile rules read them.
     * @returns {string}
     */
    get slotStyle() {
      const aspect = this.current && this.current.slotAspect;
      const turn = (this.current && this.current.slotTurn) || '90deg';
      if (!aspect) return `--bs-slot-clip: ${this.slotClip};`;

      const [w, h] = aspect.split('/').map(Number);
      const ROW_DEPTH = 43;
      const frameW = turn === '90deg' ? ROW_DEPTH : ROW_DEPTH * (w / h);
      const frameH = turn === '90deg' ? ROW_DEPTH / (w / h) : ROW_DEPTH;

      return (
        `--bs-slot-clip: ${this.slotClip};` +
        ` --bs-slot-aspect: ${aspect};` +
        ` --bs-slot-turn: ${turn};` +
        ` --bs-slot-frame-w: ${frameW.toFixed(3)}px;` +
        ` --bs-slot-half-x: ${(-frameW / 2).toFixed(3)}px;` +
        ` --bs-slot-half-y: ${(-frameH / 2).toFixed(3)}px;`
      );
    },

    /**
     * @param {string} key - Set type key
     * @returns {Array<null>} An empty slot array sized to that type's bundleSize
     */
    emptySlots(key) {
      const setType = this.data.setTypes[key];
      const size = setType ? setType.bundleSize : 0;
      return new Array(size).fill(null);
    },

    /* ---------- active set type ---------- */

    /** @returns {BuildSetType|null} */
    get current() {
      return this.activeSetType ? this.data.setTypes[this.activeSetType] : null;
    },

    /** @returns {number} */
    get bundleSize() {
      return this.current ? this.current.bundleSize : 0;
    },

    /** @returns {Array<string|null>} The active set type's slots */
    get slots() {
      return this.selections[this.activeSetType] || [];
    },

    /** @returns {BuildSetPreset[]} */
    get presetList() {
      if (!this.current || !this.current.presets) return [];
      return Object.values(this.current.presets);
    },

    /**
     * @param {string} key
     * @returns {boolean}
     */
    isActiveSetType(key) {
      return this.activeSetType === key;
    },

    /**
     * Switch set type. Selections are preserved per type (confirmed decision), so
     * this only changes which type is on screen. A set type configured with a
     * `redirectUrl` (the Mini Tin launch fallback) navigates away instead.
     * @param {string} key
     * @returns {void}
     */
    setType(key) {
      const setType = this.data.setTypes[key];
      if (!setType || key === this.activeSetType) return;

      if (setType.redirectUrl) {
        window.location.href = setType.redirectUrl;
        return;
      }

      if (!this.selections[key]) this.selections[key] = this.emptySlots(key);
      this.activeSetType = key;
      this.sheetOpen = false;
    },

    /* ---------- products ---------- */

    /**
     * @param {string} id
     * @returns {BuildSetProduct|null}
     */
    product(id) {
      return this.current && this.current.products ? this.current.products[id] : null;
    },

    /**
     * @param {string} id
     * @returns {boolean}
     */
    isSelected(id) {
      return this.slots.includes(id);
    },

    /**
     * How many slots this scent fills. A scent can repeat (client request 10/21,
     * Figma 506:121 "ADDED + ADD ANOTHER").
     * @param {string} id
     * @returns {number}
     */
    countOf(id) {
      return this.slots.filter((slot) => slot === id).length;
    },

    /**
     * @param {string} id
     * @returns {boolean}
     */
    isAvailable(id) {
      const product = this.product(id);
      return !!(product && product.available);
    },

    /**
     * A card is locked when the set is full (and this product is not in it) or
     * the product is out of stock.
     * @param {string} id
     * @returns {boolean}
     */
    isLocked(id) {
      if (this.isSelected(id)) return false;
      return this.isComplete || !this.isAvailable(id);
    },

    /** @returns {number} */
    get filledCount() {
      return this.slots.filter(Boolean).length;
    },

    /** @returns {number} */
    get remaining() {
      return Math.max(0, this.bundleSize - this.filledCount);
    },

    /** @returns {boolean} */
    get isComplete() {
      return this.bundleSize > 0 && this.filledCount === this.bundleSize;
    },

    /** @returns {number} 0-100, drives the mobile progress bar */
    get progressPercent() {
      if (!this.bundleSize) return 0;
      return Math.round((this.filledCount / this.bundleSize) * 100);
    },

    /**
     * @param {string} id
     * @returns {void}
     */
    toggleProduct(id) {
      if (this.isSelected(id)) {
        this.removeProduct(id);
        return;
      }
      this.addProduct(id);
    },

    /**
     * Put the scent in the first empty slot - again, if it is already there.
     * Used by ADD TO SET and by the card's "+" (add another).
     * @param {string} id
     * @returns {void}
     */
    addProduct(id) {
      if (this.isComplete || !this.isAvailable(id)) return;

      const slots = this.slots.slice();
      const index = slots.indexOf(null);
      if (index === -1) return;
      slots[index] = id;
      this.selections[this.activeSetType] = slots;
    },

    /**
     * Take ONE copy of the scent out - the most recently added, so a repeat comes
     * off before the original and the slots ahead of it keep their order.
     * @param {string} id
     * @returns {void}
     */
    removeProduct(id) {
      const slots = this.slots.slice();
      const index = slots.lastIndexOf(id);
      if (index === -1) return;
      slots[index] = null;
      this.selections[this.activeSetType] = slots;
    },

    /**
     * @param {number} index
     * @returns {void}
     */
    removeSlot(index) {
      if (this.slots[index] == null) return;
      const slots = this.slots.slice();
      slots[index] = null;
      this.selections[this.activeSetType] = slots;
    },

    /** @returns {void} */
    clearActiveSet() {
      this.selections[this.activeSetType] = this.emptySlots(this.activeSetType);
    },

    /** @returns {void} */
    scrollToBuilder() {
      const builder = document.querySelector('[data-build-set-builder]');
      if (!builder) return;
      builder.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },

    /* ---------- pricing ----------
       The children carry the price, not the parent: a set is simply the sum of
       the scents in it (3 x $14 = $42). The parent variant is a $0 container.
       The set type's `bundlePrice` is no longer the charge - it survives in
       BuildSetData only as merchant-facing copy. */

    /** @returns {number} Sum of the selected products, in cents */
    get subtotal() {
      return this.slots.reduce((sum, id) => {
        const product = id ? this.product(id) : null;
        return sum + (product ? product.price : 0);
      }, 0);
    },

    /**
     * Unit price of the pool. Every product in a pool is the same format at the
     * same price (all 6ml are $14), so the first one speaks for the rest - this
     * is what lets the full set price show before anything is selected.
     * @returns {number} In cents, 0 for an empty pool
     */
    get unitPrice() {
      const products = this.current ? Object.values(this.current.products || {}) : [];
      return products.length ? products[0].price : 0;
    },

    /**
     * What the finished set costs: what is already chosen, plus the empty slots
     * at the pool's unit price. Equals `subtotal` once the set is complete.
     * @returns {number} In cents
     */
    get displayTotal() {
      return this.subtotal + this.remaining * this.unitPrice;
    },

    /**
     * The same projection against compare-at prices, so a genuine markdown on
     * the scents still reads as a saving.
     * @returns {number|null} In cents, or null when no product is marked down
     */
    get comparePrice() {
      const products = this.current ? Object.values(this.current.products || {}) : [];
      const unit = products.length ? products[0].compareAtPrice || products[0].price : 0;
      let marked = false;
      const chosen = this.slots.reduce((sum, id) => {
        const product = id ? this.product(id) : null;
        if (!product) return sum;
        if (product.compareAtPrice > product.price) marked = true;
        return sum + (product.compareAtPrice || product.price);
      }, 0);
      if (unit > this.unitPrice) marked = true;
      return marked ? chosen + this.remaining * unit : null;
    },

    /** @returns {number} In cents */
    get savings() {
      const compare = this.comparePrice;
      return compare == null ? 0 : Math.max(0, compare - this.displayTotal);
    },

    /**
     * @param {number} cents
     * @returns {string}
     */
    money(cents) {
      if (window.Shopify && Shopify.formatMoney) {
        return Shopify.formatMoney(cents, document.body.dataset.moneyFormat);
      }
      return `$${(cents / 100).toFixed(2)}`;
    },

    /**
     * Keep Tab inside the mobile panel while it is open.
     *
     * The panel is the dialog in practice - the sheet holds the slots but the
     * add-to-cart button and the close toggle sit alongside it, and a trap that
     * covered only the sheet would strand a keyboard user with no way to buy or
     * close without leaving the dialog first.
     * @param {KeyboardEvent} event
     * @param {HTMLElement} panel
     * @returns {void}
     */
    trapFocus(event, panel) {
      if (!panel) return;
      const focusable = [...panel.querySelectorAll(
        'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )].filter((el) => el.offsetParent !== null);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },

    /**
     * The card button's current wording, for its accessible name.
     *
     * Every card's button reads the same four words, so a screen reader hears
     * "add to set" eight times with nothing to tell them apart. Pairing the
     * wording with the scent's name gives each control a distinct name, and
     * keeping the visible text as the prefix means speech-input users can still
     * say what they see.
     * @param {string} id - Product id
     * @returns {string}
     */
    cardLabel(id) {
      const labels = this.data.labels || {};
      /* No English fallbacks: these are merchant settings, already written in the
         storefront's language. Inventing a default here would put untranslated
         English into an accessible name on a localised storefront, so a missing
         setting simply contributes nothing and the product's name carries it. */
      if (this.isSelected(id)) return labels.cardAdded || '';
      if (!this.isAvailable(id)) return labels.cardSoldOut || '';
      if (this.isComplete) return labels.cardComplete || '';
      return labels.cardAdd || '';
    },

    /**
     * @param {string} id - Product id
     * @returns {string} Accessible name for that card's button
     */
    cardAriaLabel(id) {
      const product = this.product(id);
      return [this.cardLabel(id) + this.countSuffix(id), product ? product.title : '']
        .filter(Boolean)
        .join(' ');
    },

    /**
     * The " · 2" after ✓ ADDED once a scent is in the set more than once. The
     * accessible name carries it too, so it matches what is on screen.
     * @param {string} id
     * @returns {string}
     */
    countSuffix(id) {
      const count = this.countOf(id);
      return count > 1 ? ` · ${count}` : '';
    },

    /**
     * @param {string} id - Product id
     * @returns {string} Accessible name for the card's "+" (add another)
     */
    addAnotherLabel(id) {
      const product = this.product(id);
      const labels = this.data.labels || {};
      return [labels.addAnother, product ? product.title : ''].filter(Boolean).join(' ');
    },

    /** @returns {string} "ADD 1 MORE" while incomplete, then the add-to-cart label. */
    get ctaLabel() {
      const labels = this.data.labels || {};
      if (this.addingToCart) return labels.adding || 'ADDING...';
      if (!this.isComplete) {
        return (labels.addMore || 'ADD [count] MORE').replace('[count]', this.remaining);
      }
      return labels.addToCart || 'ADD SET TO CART';
    },

    /* ---------- cart ---------- */

    /**
     * @param {number} [length] - Character count (default 5)
     * @returns {string} A short random code shared by one bundle's cart lines
     */
    uniqueCode(length = 5) {
      let code = '';
      while (code.length < length) {
        code += Math.floor(Math.random() * 1000).toString(36);
      }
      return code.substring(0, length);
    },

    /**
     * Shared payload builder for both the self-built set and a locked curated set.
     * Children go in reverse so the customer's chosen order survives the cart's
     * newest-first ordering; the parent goes last carrying _bundle_skus.
     * @param {(BuildSetProduct|null|undefined)[]} products - Product records, not ids
     * @param {number|null} parentVariantId
     * @returns {CartLineItem[]}
     */
    buildBundleItems(products, parentVariantId) {
      const chosen = products.filter(Boolean);
      if (!chosen.length) return [];

      const code = this.uniqueCode();
      const skus = chosen.map((product) => product.sku || '').filter(Boolean);
      const items = [];

      for (let i = chosen.length - 1; i >= 0; i--) {
        items.push({
          id: chosen[i].variantId,
          quantity: 1,
          properties: { _bundle: code, _id: this.uniqueCode() },
        });
      }

      if (parentVariantId) {
        items.push({
          id: parentVariantId,
          quantity: 1,
          properties: { _bundle: code, _bundle_skus: skus.join(',') },
        });
      }

      return items;
    },

    /**
     * @param {CartLineItem[]} items
     * @returns {Promise<Object>} The /cart/add.js response
     * @throws {Error} When the cart rejects the payload
     */
    async postItems(items) {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        let description = '';
        try {
          const error = await response.json();
          description = error && error.description;
        } catch (parseError) {
          description = '';
        }
        throw new Error(description || 'Could not add your set to cart.');
      }

      return response.json();
    },

    /**
     * Add the set the customer built in the grid.
     * @returns {Promise<void>}
     */
    async addToCart() {
      if (!this.isComplete || this.addingToCart || !this.current) return;

      const items = this.buildBundleItems(
        this.slots.filter(Boolean).map((id) => this.product(id)),
        this.current.parentVariantId
      );
      if (!items.length) return;

      this.addingToCart = true;

      try {
        await this.postItems(items);
        await this.openCart();
        this.clearActiveSet();
        this.sheetOpen = false;
      } catch (error) {
        this.reportError(error);
      } finally {
        this.addingToCart = false;
      }
    },

    /**
     * Add a curated set straight to the cart as a locked set.
     * Deliberately does NOT touch the builder (confirmed client decision).
     * @param {string} handle - Metaobject handle of the curated set
     * @returns {Promise<void>}
     */
    async addCuratedSetToCart(handle) {
      if (this.addingPreset || !this.current) return;

      const preset = this.current.presets ? this.current.presets[handle] : null;
      if (!preset) return;

      const items = this.buildBundleItems(preset.products, this.current.parentVariantId);
      if (!items.length) return;

      this.addingPreset = handle;

      try {
        await this.postItems(items);
        await this.openCart();
      } catch (error) {
        this.reportError(error);
      } finally {
        this.addingPreset = null;
      }
    },

    /**
     * @param {Error} error
     * @returns {void}
     */
    reportError(error) {
      console.error('[build-set] add to cart failed', error);
      window.alert(error && error.message ? error.message : 'Could not add your set to cart.');
    },

    /**
     * Theme-wide drawer contract: refresh the cart, then put `cart-active` on
     * <html> (see snippets/cart-drawer-js.liquid).
     * @returns {Promise<void>}
     */
    async openCart() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        if (typeof window.refreshCart === 'function') window.refreshCart(cart);
      } catch (error) {
        console.error('[build-set] could not refresh the cart', error);
      } finally {
        document.documentElement.classList.add('cart-active');
      }
    },
  });
});
